import React, { useState } from "react";
import { Trash2, Shuffle, Lock, Unlock, RefreshCw, Loader2, Brain, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, sceneVideoUrl, fmtDuration } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";

const SceneRow = ({ pid, sc, active, onPlay, onChanged }) => {
  const [busy, setBusy] = useState("");
  const act = async (k, fn, msg) => {
    setBusy(k);
    try { await fn(); toast.success(msg); await onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  return (
    <div className={`flex items-center gap-3 p-2 rounded-lg border transition-colors ${active ? "border-[#E6B04E] bg-[#212534]" : "border-[#252A3B] hover:border-[#3B4259]"}`}
      data-testid={`wizard-scene-${sc.id}`}>
      <button onClick={() => sc.rendered && onPlay(sc)} className="relative shrink-0" disabled={!sc.rendered} data-testid={`wizard-scene-play-${sc.id}`}>
        {sc.thumb_media_id ? <img src={assetUrl(sc.thumb_media_id, "thumbnail")} alt="" className="w-24 h-14 object-cover rounded" /> : <div className="w-24 h-14 bg-[#212534] rounded" />}
        {sc.rendered && <PlayCircle size={22} className="absolute inset-0 m-auto text-white drop-shadow" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate">{sc.title}</div>
        <div className="metric-label truncate">{sc.event} · {sc.mood} · {sc.shots} shots · {Math.round(sc.seconds || 0)}s · {sc.rendered ? `rendered ${sc.render_res}` : "rendering…"}</div>
      </div>
      <button title={sc.locked ? "Unlock" : "Keep this scene (lock)"} className={`btn btn-ghost !p-1.5 ${sc.locked ? "text-[#E6B04E]" : ""}`} disabled={!!busy}
        onClick={() => act("lock", () => api.lockScene(pid, sc.id, !sc.locked), sc.locked ? "Unlocked" : "Scene kept")} data-testid={`wizard-scene-lock-${sc.id}`}>
        {sc.locked ? <Lock size={14} /> : <Unlock size={14} />}</button>
      <button title="Swap shots" className="btn btn-ghost !p-1.5" disabled={!!busy || sc.locked}
        onClick={() => act("swap", () => api.regenScene(pid, sc.id), "New shots picked. Re-render to see it.")} data-testid={`wizard-scene-swap-${sc.id}`}>
        {busy === "swap" ? <Loader2 size={14} className="animate-spin" /> : <Shuffle size={14} />}</button>
      <button title="Remove scene" className="btn btn-ghost !p-1.5 hover:text-[#EF4444]" disabled={!!busy || sc.locked}
        onClick={() => act("rm", () => api.wizardRemoveScene(pid, sc.id), "Scene removed")} data-testid={`wizard-scene-remove-${sc.id}`}>
        <Trash2 size={14} /></button>
    </div>
  );
};

export const StepReview = ({ pid, w, onNext }) => {
  const { data, refresh } = usePoll(() => api.wizardScenes(pid), 6000, [pid]);
  const [playing, setPlaying] = useState(null);
  const [busy, setBusy] = useState(false);
  const scenes = (data?.chapters || []).flatMap((c) => c.scenes.map((s) => ({ ...s, chapter: c.title })));
  const rendered = scenes.filter((s) => s.rendered).length;
  const rendering = w.render_job?.status === "running" || w.render_job?.status === "queued";
  const cur = playing || scenes.find((s) => s.rendered);

  const rerender = async () => {
    setBusy(true);
    try { await api.wizardRerender(pid); toast.success("Re-rendering changed scenes (unchanged ones are reused)"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const next = async () => { setBusy(true); try { await onNext(); } finally { setBusy(false); } };
  const onEnded = () => {
    const i = scenes.findIndex((s) => s.id === cur?.id);
    const nxt = scenes.slice(i + 1).find((s) => s.rendered);
    if (nxt) setPlaying(nxt);
  };

  if (!scenes.length) return <div className="text-[#94A3B8] text-sm" data-testid="wizard-panel-review">The edit plan isn't ready yet. Finish Step 2 first.</div>;
  return (
    <div className="grid xl:grid-cols-[1.5fr_1fr] gap-4" data-testid="wizard-panel-review">
      <div className="space-y-3">
        {cur ? (
          <video key={cur.id + (cur.render_res || "")} src={sceneVideoUrl(pid, cur.id, cur.render_res)} controls autoPlay={!!playing} onEnded={onEnded}
            className="w-full rounded-lg bg-black aspect-video" data-testid="wizard-scene-player" />
        ) : <div className="aspect-video rounded-lg bg-[#0d0f16] flex items-center justify-center text-[#64748B] text-sm"><Loader2 size={16} className="animate-spin mr-2" />First scene is rendering…</div>}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm"><span className="font-semibold" data-testid="wizard-now-playing">{cur?.title || "-"}</span>
            <span className="metric-label ml-2">{cur?.chapter}</span></div>
          <div className="metric-label" data-testid="wizard-render-count">{rendered}/{scenes.length} scenes rendered · film ~{fmtDuration(data?.estimated_seconds || 0)}{rendering ? ` · rendering ${w.render_job.progress || 0}%` : ""}</div>
        </div>
        <div className="flex gap-2 justify-end flex-wrap">
          <button className="btn btn-ghost" onClick={rerender} disabled={busy || rendering} data-testid="btn-wizard-rerender"><RefreshCw size={14} />Re-render changes</button>
          <button className="btn btn-gold" onClick={next} disabled={busy || w.steps.rough_cut.status !== "done"} data-testid="btn-wizard-to-deep">
            <Brain size={15} />Approve, next: Deep AI on {scenes.reduce((a, s) => a + s.shots, 0)} shots</button>
        </div>
      </div>
      <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="wizard-scene-list">
        {scenes.map((sc) => <SceneRow key={sc.id} pid={pid} sc={sc} active={cur?.id === sc.id} onPlay={setPlaying} onChanged={refresh} />)}
      </div>
    </div>
  );
};
